import { useState } from 'react';
import { useApp } from '../context/useApp';
import { useAuth } from '../context/useAuth';
import { useLanguage } from '../i18n';
import LanguageSwitcher from './LanguageSwitcher';
import { Check, AlertCircle, Zap, Crown, FileText, LogOut, ArrowUpRight, Globe } from 'lucide-react';

interface SettingsPanelProps {
  onOpenUpgrade?: () => void;
}

export default function SettingsPanel({ onOpenUpgrade }: SettingsPanelProps) {
  const { settings, setSettings } = useApp();
  const { user, logout, updateLocale } = useAuth();
  const { t, locale } = useLanguage();
  const s = t.settings;
  const [testStatus, setTestStatus] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState('');

  const testAI = async () => {
    setTestStatus('loading');
    setTestMessage('');
    try {
      const response = await fetch('/api/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: locale === 'de' ? 'Sag „KI ist bereit“ und sonst nichts.' : 'Say "AI is ready" and nothing else.',
          section: 'test',
          locale,
        }),
      });
      const data = (await response.json()) as { error?: string; content?: string; code?: string };
      if (!response.ok || data.error) {
        setTestStatus('error');
        setTestMessage(data.error || `HTTP ${response.status}`);
      } else {
        setTestStatus('ok');
        setTestMessage(`${locale === 'de' ? 'KI antwortet.' : 'AI is responding.'} Output: "${(data.content || '').trim().slice(0, 40)}"`);
      }
    } catch (err) {
      setTestStatus('error');
      setTestMessage(err instanceof Error ? err.message : 'Network error');
    }
  };

  const used = user?.generations_used ?? 0;
  const limit = user?.generations_limit ?? 0;
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;

  return (
    <div className="h-full overflow-auto p-4 sm:p-6 md:p-8 safe-bottom">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-ink mb-6">{s.title}</h1>

        <div className="bg-white border border-cloud p-4 sm:p-6 mb-6">
          <div className="flex items-center gap-3 sm:gap-4 mb-5 flex-wrap">
            <div className="w-12 h-12 bg-red-50 flex items-center justify-center shrink-0">
              <Zap className="text-red-500" size={24} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold truncate">{s.currentPlan}: {user ? t.plans[user.plan] : '—'}</h2>
              <p className="text-sm text-slate truncate">{user?.email}</p>
            </div>
            <span className="px-3 py-1 text-xs font-semibold bg-red-500 text-white">
              {user ? t.plans[user.plan] : '—'}
            </span>
          </div>

          {user?.plan === 'starter' && onOpenUpgrade && (
            <button
              onClick={onOpenUpgrade}
              className="w-full min-h-[44px] mt-5 py-2.5 bg-red-500 hover:bg-red-600 text-white font-medium flex items-center justify-center gap-2 transition-colors"
            >
              {t.project.upgradeNow}
              <ArrowUpRight size={16} />
            </button>
          )}

          <div className="border-t border-mist pt-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate">{s.aiRequests}</span>
              <span className="text-sm font-semibold text-ink">
                {used.toLocaleString()} / {limit.toLocaleString()}
              </span>
            </div>
            <div className="h-2 bg-mist border border-cloud overflow-hidden mb-5">
              <div className="h-full bg-red-500 transition-all duration-300" style={{ width: `${pct}%` }} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-cloud p-4">
                <div className="flex items-center gap-2 mb-2">
                  <FileText size={18} className="text-red-500" />
                  <span className="font-medium">{s.planLabels.starter}</span>
                </div>
                <ul className="text-sm text-slate space-y-1">
                  {s.planFeatures.starter.map((item: string) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="border border-red-100 bg-red-50 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Crown size={18} className="text-red-500" />
                  <span className="font-medium">Founder / Scale</span>
                  <ArrowUpRight size={16} className="text-red-500" />
                </div>
                <ul className="text-sm text-slate space-y-1">
                  {s.planFeatures.paid.map((item: string) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-cloud p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">{t.language.label}</h2>
          <p className="text-sm text-slate mb-4">
            {locale === 'de'
              ? 'Wähle die Sprache der App und der KI-Ausgaben.'
              : 'Choose the language for the app and AI outputs.'}
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <Globe size={18} className="text-slate" />
            <LanguageSwitcher onChange={updateLocale} />
          </div>
        </div>

        <div className="bg-white border border-cloud p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">{s.aiStatus}</h2>
          <p className="text-sm text-slate mb-4">{s.aiDescription}</p>
          <button
            onClick={testAI}
            disabled={testStatus === 'loading'}
            className="min-h-[44px] px-4 py-2 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-medium transition-colors flex items-center gap-2"
          >
            {testStatus === 'loading' ? s.testing : s.testAi}
          </button>
          {testStatus === 'ok' && (
            <div className="mt-3 flex items-start gap-2 p-3 bg-emerald-50 text-emerald-800 border border-emerald-100 text-sm">
              <Check size={16} className="shrink-0 mt-0.5" />
              <span>{testMessage}</span>
            </div>
          )}
          {testStatus === 'error' && (
            <div className="mt-3 flex items-start gap-2 p-3 bg-red-50 text-red-800 border border-red-100 text-sm">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <span>{testMessage}</span>
            </div>
          )}
        </div>

        <div className="bg-white border border-cloud p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">{s.appearance}</h2>
          <label className="flex items-center gap-3 cursor-pointer min-h-[44px]">
            <input
              type="checkbox"
              checked={settings.darkMode}
              onChange={(e) => setSettings({ ...settings, darkMode: e.target.checked })}
              className="w-4 h-4 text-red-500 focus:ring-red-500"
            />
            <span className="text-ink">{s.darkMode}</span>
          </label>
        </div>

        <div className="bg-white border border-cloud p-4 sm:p-6">
          <button
            onClick={() => logout()}
            className="w-full min-h-[44px] flex items-center justify-center gap-2 px-4 py-3 text-ink hover:text-red-600 hover:bg-red-50 border border-cloud font-medium transition-colors"
          >
            <LogOut size={18} />
            {t.nav.logout}
          </button>
        </div>
      </div>
    </div>
  );
}
